// Relative-time formatting. Pure: callers pass `now`, locale and time zone (tests pin them). A
// caller that passes no zone gets the owner's (Settings → Time zone, set by the shell once it
// loads), so every time on screen reads in the same zone as briefs and "today".

export interface TimeFormatOptions {
  locale?: string | undefined;
  timeZone?: string | undefined;
}

let displayZone: string | undefined;

/** The zone used when a caller passes none; an unknown zone is ignored (the browser's stays). */
export function setDisplayTimeZone(zone: string | null | undefined): void {
  if (!zone) {
    displayZone = undefined;
    return;
  }
  try {
    new Intl.DateTimeFormat("en", { timeZone: zone });
    displayZone = zone;
  } catch {
    // Not a zone this browser knows.
  }
}

function withZone(options: TimeFormatOptions): TimeFormatOptions {
  return options.timeZone || !displayZone ? options : { ...options, timeZone: displayZone };
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Calendar day key (YYYY-MM-DD) in the given zone, used to compare "today"/"yesterday". */
function dayKey(date: Date, options: TimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: options.timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function yearOf(date: Date, options: TimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: options.timeZone, year: "numeric" }).format(
    date,
  );
}

/**
 * Compact timestamp for list rows: "now", "12m", "14:05" (today), "Yesterday",
 * "Tue" (this week), "Sep 3" (this year), "Sep 3, 2024".
 */
export function formatListTime(
  value: string | null | undefined,
  now: number | Date,
  rawOptions: TimeFormatOptions = {},
): string {
  const options = withZone(rawOptions);
  const date = toDate(value);
  if (!date) return "";
  const nowDate = now instanceof Date ? now : new Date(now);
  const diff = nowDate.getTime() - date.getTime();
  const { locale, timeZone } = options;

  if (diff >= 0 && diff < MINUTE) return "now";
  if (diff >= 0 && diff < HOUR) return `${Math.floor(diff / MINUTE)}m`;
  if (dayKey(date, options) === dayKey(nowDate, options)) {
    return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit", timeZone }).format(
      date,
    );
  }
  if (dayKey(date, options) === dayKey(new Date(nowDate.getTime() - DAY), options)) {
    return "Yesterday";
  }
  if (diff > 0 && diff < 6 * DAY) {
    return new Intl.DateTimeFormat(locale, { weekday: "short", timeZone }).format(date);
  }
  if (yearOf(date, options) === yearOf(nowDate, options)) {
    return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", timeZone }).format(
      date,
    );
  }
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  }).format(date);
}

/** Freshness wording: "just now", "12 s ago", "3 min ago", "2 h ago", "3 days ago", or `fallback`. */
export function formatAgo(
  value: string | null | undefined,
  now: number | Date,
  fallback = "not yet",
): string {
  const date = toDate(value);
  if (!date) return fallback;
  const nowMs = now instanceof Date ? now.getTime() : now;
  const diff = Math.max(0, nowMs - date.getTime());
  if (diff < 10 * SECOND) return "just now";
  if (diff < MINUTE) return `${Math.floor(diff / SECOND)} s ago`;
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)} min ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)} h ago`;
  const days = Math.floor(diff / DAY);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

/** Full, unambiguous timestamp for message headers and tooltips. */
export function formatFull(
  value: string | null | undefined,
  rawOptions: TimeFormatOptions = {},
): string {
  const options = withZone(rawOptions);
  const date = toDate(value);
  if (!date) return "";
  return new Intl.DateTimeFormat(options.locale, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: options.timeZone,
  }).format(date);
}

/** Short date and time for sync-gap explanations, e.g. "Sep 3, 14:05" or "Sep 3, 2:05 PM". */
export function formatShortDateTime(
  value: string | null | undefined,
  rawOptions: TimeFormatOptions = {},
): string {
  const options = withZone(rawOptions);
  const date = toDate(value);
  if (!date) return "an unknown time";
  // Format the day and the clock time separately and join them here: ICU's combined
  // date-time pattern varies between platform builds, and this wording has to read the
  // same everywhere.
  const day = new Intl.DateTimeFormat(options.locale, {
    month: "short",
    day: "numeric",
    timeZone: options.timeZone,
  }).format(date);
  const clock = new Intl.DateTimeFormat(options.locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: options.timeZone,
  }).format(date);
  return `${day}, ${clock}`;
}

/** How often a relative label needs re-rendering to stay truthful. */
export function refreshIntervalFor(value: string | null | undefined, now: number): number {
  const date = toDate(value);
  if (!date) return 60 * SECOND;
  const diff = now - date.getTime();
  if (diff < MINUTE) return 5 * SECOND;
  if (diff < HOUR) return 30 * SECOND;
  return 5 * MINUTE;
}
